import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useWorkspace } from '@/hooks/useWorkspace';
import {
  exportMapToJson,
  importMapFromJson,
} from '@/services/mapExportImport';
import type {
  ThematicMap,
  ThematicMapWithStats,
  NewThematicMap,
  UpdateThematicMap,
  MapStatus,
} from '@/types/thematicMaps';

export const useThematicMaps = () => {
  const [maps, setMaps] = useState<ThematicMapWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const { workspace } = useWorkspace();

  const fetchMaps = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      // Pobierz mapy BEZ filtrowania po user_id (dane shared), FILTER po workspace
      const { data, error } = await (supabase as any)
        .from('thematic_maps')
        .select(`
          *,
          thematic_seeds(count),
          thematic_nodes(count),
          thematic_entities(count),
          thematic_node_relations(count),
          websites(name, url, schema_enabled)
        `)
        .eq('workspace', workspace)
        .is('map_kind', null)  // standalone mode: ukryj mapy z map_kind='standalone'
        .order('created_at', { ascending: false });

      if (error) throw error;

      const rows: any[] = data || [];
      const mapIds: string[] = rows.map((m) => m.id);
      const profileIds: string[] = rows.map((m) => m.active_profile_id).filter(Boolean);

      // --- Batchowe staty per mapa (wzorzec z useDashboardStats: pobierz surowe wiersze
      //     filtrowane po map_id, zlicz w JS). allSettled = odporność: padnięta kwerenda
      //     (np. RLS na schema_injection_logs) nie wywala pozostałych statów ani kart. ---
      const [nodesR, contentR, schemaR, profilesR] = mapIds.length
        ? await Promise.allSettled([
            (supabase as any).from('thematic_nodes').select('id, map_id, node_role').in('map_id', mapIds),
            (supabase as any).from('thematic_node_content').select('map_id, engine_stage, indexed_at').in('map_id', mapIds),
            (supabase as any).from('schema_injection_logs').select('node_id, schemas_injected'),
            (supabase as any).from('thematic_writing_profiles').select('id, name, author_identity').in('id', profileIds),
          ])
        : [];
      const nodeRows: any[] = nodesR?.status === 'fulfilled' ? (nodesR.value.data || []) : [];
      const contentRows: any[] = contentR?.status === 'fulfilled' ? (contentR.value.data || []) : [];
      const schemaRows: any[] = schemaR?.status === 'fulfilled' ? (schemaR.value.data || []) : [];
      const profileRows: any[] = profilesR?.status === 'fulfilled' ? (profilesR.value.data || []) : [];

      // nazwa Writing Profile per id (name → fallback author_identity)
      const profileNameById = new Map<string, string>();
      for (const pr of profileRows) {
        const nm = (pr.name && String(pr.name).trim()) || (pr.author_identity && String(pr.author_identity).trim()) || '';
        if (nm) profileNameById.set(pr.id, nm);
      }

      // node_role per mapa + mapowanie node_id -> map_id (do atrybucji schemy)
      const rolesByMap = new Map<string, { pillar: number; hub: number; spoke: number; money: number }>();
      const nodeToMap = new Map<string, string>();
      for (const n of nodeRows) {
        nodeToMap.set(n.id, n.map_id);
        const r = rolesByMap.get(n.map_id) ?? { pillar: 0, hub: 0, spoke: 0, money: 0 };
        if (n.node_role === 'pillar') r.pillar += 1;
        else if (n.node_role === 'hub') r.hub += 1;
        else if (n.node_role === 'spoke') r.spoke += 1;
        else if (n.node_role === 'money_node') r.money += 1;
        rolesByMap.set(n.map_id, r);
      }

      // pipeline per mapa (engine_stage). Nieznany stage -> tylko total (defensywnie).
      const WORKING = new Set([
        'serp_research', 'kg_assembly', 'fan_out', 'outline',
        'draft', 'enrichment', 'optimization', 'humanization',
      ]);
      const pipeByMap = new Map<string, ReturnType<typeof emptyPipe>>();
      function emptyPipe() {
        return { total: 0, pending: 0, working: 0, brief: 0, ready: 0, published: 0, indexed: 0, skipped: 0, failed: 0 };
      }
      for (const c of contentRows) {
        const p = pipeByMap.get(c.map_id) ?? emptyPipe();
        p.total += 1;
        const s = c.engine_stage as string;
        if (s === 'published') {
          p.published += 1;
          if (c.indexed_at) p.indexed += 1;
        }
        else if (s === 'ready') p.ready += 1;
        else if (s === 'outline_ready') p.brief += 1;
        else if (s === 'skipped_existing') p.skipped += 1;
        else if (s === 'failed') p.failed += 1;
        else if (s === 'pending') p.pending += 1;
        else if (WORKING.has(s)) p.working += 1;
        pipeByMap.set(c.map_id, p);
      }

      // schema per mapa: distinct node_id z niepustym schemas_injected -> przez nodeToMap
      const schemaNodesByMap = new Map<string, Set<string>>();
      for (const sl of schemaRows) {
        const mid = nodeToMap.get(sl.node_id);
        if (!mid) continue;
        const inj = sl.schemas_injected;
        const has = Array.isArray(inj)
          ? inj.length > 0
          : inj && typeof inj === 'object'
            ? Object.keys(inj).length > 0
            : !!inj;
        if (!has) continue;
        const set = schemaNodesByMap.get(mid) ?? new Set<string>();
        set.add(sl.node_id);
        schemaNodesByMap.set(mid, set);
      }

      const mapsWithStats: ThematicMapWithStats[] = rows.map((m: any) => {
        const activityTs = [m.updated_at, m.audited_at, m.last_repaired_at, m.wp_posts_synced_at, m.last_generation_at]
          .filter(Boolean)
          .map((d: string) => new Date(d).getTime());
        const lastActivity = activityTs.length ? new Date(Math.max(...activityTs)).toISOString() : m.updated_at;
        return {
          id: m.id,
          user_id: m.user_id,
          website_id: m.website_id,
          name: m.name,
          macro_statement: m.macro_statement,
          scope_in: m.scope_in || [],
          scope_out: m.scope_out || [],
          audience: m.audience || '',
          language: m.language || 'pl',
          map_version: m.map_version || 'v1',
          status: m.status as MapStatus,
          active_profile_id: m.active_profile_id ?? null,
          audit_report: m.audit_report ?? {},
          audit_score: m.audit_score ?? null,
          audited_at: m.audited_at ?? null,
          wp_posts_synced_at: m.wp_posts_synced_at ?? null,
          created_at: m.created_at,
          updated_at: m.updated_at,
          seeds_count: m.thematic_seeds?.[0]?.count ?? 0,
          nodes_count: m.thematic_nodes?.[0]?.count ?? 0,
          entities_count: m.thematic_entities?.[0]?.count ?? 0,
          relations_count: m.thematic_node_relations?.[0]?.count ?? 0,
          website_name: m.websites?.name,
          website_url: m.websites?.url,
          schema_enabled: !!m.websites?.schema_enabled,
          pipeline: pipeByMap.get(m.id) ?? emptyPipe(),
          roles: rolesByMap.get(m.id) ?? { pillar: 0, hub: 0, spoke: 0, money: 0 },
          schema_count: schemaNodesByMap.get(m.id)?.size ?? 0,
          has_profile: !!m.active_profile_id,
          profile_name: m.active_profile_id ? (profileNameById.get(m.active_profile_id) ?? null) : null,
          audit_llm_used: !!(m.audit_report?.meta?.llm_used),
          repair_status: m.repair_status ?? null,
          last_repaired_at: m.last_repaired_at ?? null,
          last_activity: lastActivity,
        } as ThematicMapWithStats;
      });

      setMaps(mapsWithStats);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd pobierania map';
      setError(msg);
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [user?.id, toast, workspace]);

  const getMap = useCallback(async (id: string): Promise<ThematicMap | null> => {
    try {
      const { data, error } = await (supabase as any)
        .from('thematic_maps')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      // Defensive: JSONB columns may come as null from Supabase
      return {
        ...data,
        scope_in: data.scope_in || [],
        scope_out: data.scope_out || [],
        audience: data.audience || '',
        language: data.language || 'pl',
      } as ThematicMap;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd pobierania mapy';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return null;
    }
  }, [toast]);

  const createMap = useCallback(async (mapData: Omit<NewThematicMap, 'user_id'>): Promise<ThematicMap | null> => {
    if (!user) return null;

    try {
      const { data, error } = await (supabase as any)
        .from('thematic_maps')
        .insert({
          user_id: user.id,
          workspace,
          ...mapData,
        })
        .select()
        .single();

      if (error) throw error;

      toast({ title: 'Sukces', description: 'Mapa tematyczna została utworzona' });
      await fetchMaps();
      return data as ThematicMap;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd tworzenia mapy';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return null;
    }
  }, [user, toast, fetchMaps]);

  const updateMap = useCallback(async (id: string, updates: UpdateThematicMap): Promise<boolean> => {
    try {
      const { data, error } = await (supabase as any)
        .from('thematic_maps')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      if (!data) {
        throw new Error('Brak danych po zapisie — sprawdź czy masz uprawnienia (allowed_users)');
      }

      // Aktualizuj lokalny cache z danymi z DB (nie z updates — bo DB moze zmodyfikowac)
      setMaps(prev => prev.map(m =>
        m.id === id ? {
          ...m,
          ...updates,
          scope_in: data.scope_in || [],
          scope_out: data.scope_out || [],
        } : m
      ));
      toast({ title: 'Sukces', description: 'Mapa została zaktualizowana' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd aktualizacji mapy';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  const deleteMap = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_maps')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setMaps(prev => prev.filter(m => m.id !== id));
      toast({ title: 'Sukces', description: 'Mapa została usunięta' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd usuwania mapy';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  // Realtime — BEZ filtra user_id (shared)
  useEffect(() => {
    if (!user) return;

    const subscription = supabase
      .channel('thematic_maps_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'thematic_maps',
        filter: 'map_kind=is.null',  // pomijaj zmiany ukrytych standalone map
      }, () => {
        fetchMaps();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user?.id, fetchMaps]);

  useEffect(() => {
    if (user?.id) {
      fetchMaps();
    }
  }, [user?.id, fetchMaps]);

  /**
   * Clone mapy w DB. Używa pod spodem exportMapToJson + importMapFromJson
   * żeby mieć JEDEN silnik (DRY). Pełna kopia: map + seeds + nodes + entities
   * + entity_attributes (EAV) + relations + writing_profiles + audit/repair
   * report + paa_preview. Zachowuje matched_wp_post_id (ta sama DB + te same
   * wp_posts istnieją).
   *
   * Sygnatura zachowana dla backward compat (MapsList.tsx).
   */
  const cloneMap = useCallback(
    async (sourceMapId: string, newName: string): Promise<ThematicMap | null> => {
      if (!user) return null;

      try {
        const payload = await exportMapToJson(sourceMapId);
        const result = await importMapFromJson(payload, {
          newName,
          // websiteId: undefined → zachowaj źródłowy
          preserveWpMatches: true, // ta sama DB, wp_posts istnieją
          status: "draft",
        });

        toast({
          title: "Mapa sklonowana",
          description: `"${newName}" — ${result.counts.seeds} seeds · ${result.counts.nodes} nodes · ${result.counts.entities} entities · ${result.counts.relations} relacji · ${result.counts.wp_matches_preserved} WP matches`,
        });
        await fetchMaps();

        // Zwróć pełny wiersz mapy (sygnatura ThematicMap | null)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: newMap } = await (supabase as any)
          .from("thematic_maps")
          .select("*")
          .eq("id", result.newMapId)
          .maybeSingle();
        return (newMap as ThematicMap) || null;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Błąd klonowania mapy";
        toast({ title: "Błąd", description: msg, variant: "destructive" });
        return null;
      }
    },
    [user, toast, fetchMaps],
  );

  return {
    maps,
    loading,
    error,
    getMap,
    createMap,
    updateMap,
    deleteMap,
    cloneMap,
    refreshMaps: fetchMaps,
  };
};
